import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Briefcase } from 'lucide-react';
import { API_ENDPOINTS } from '../services/apiEndpoints';
import { api1 } from '../utils/axiosInstance';
import JobsList from '../components/JobsList/JobsList';
import { usCities, canadaCities } from '../constants/jobCities';
import FilterDropdown from '../components/commonDropDown/FilterDropdown';

const AllJobs = () => {
  const currentUserJob = useSelector((state) => state.user.currentJob);
  
  const [jobs, setJobs] = useState([]);
  const [savedJobs, setSavedJobs] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [filters, setFilters] = useState({
    country: 'us',
    city: '',
    remote: ''
  });
  const [pagination, setPagination] = useState({
    totalResults: 0,
    hasMore: false,
    limit: 20
  });

  const getCitiesForCountry = () => {
    if (filters.country === 'us') return usCities;
    if (filters.country === 'ca') return canadaCities;
    return [];
  };

  useEffect(() => {
    if (!filters.country) {
      setFilters(prev => ({ ...prev, city: '' }));
    }
  }, [filters.country]);

  useEffect(() => {
    if (currentUserJob) {
      setCurrentPage(0);
      fetchJobsAndSavedStatus(0, false);
    }
  }, [currentUserJob, filters]);

  const fetchJobsAndSavedStatus = async (page = 0, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const [jobsResponse, savedJobsResponse] = await Promise.all([
        api1.post(API_ENDPOINTS.ALL_JOBS, {
          currentJob: currentUserJob,
          country: filters.country || 'us',
          city: filters.city || null,
          remote: filters.remote || null,
          page: page
        }),
        api1.get('/api/saved-jobs', {
          params: {
            page: 0,
            limit: 1000
          }
        })
      ]);

      if (jobsResponse.data.success) {
        const newJobs = jobsResponse.data.data.jobs;
        
        if (append) {
          setJobs(prevJobs => [...prevJobs, ...newJobs]);
        } else {
          setJobs(newJobs);
        }
        
        setPagination({
          totalResults: jobsResponse.data.data.totalResults,
          hasMore: jobsResponse.data.data.hasMore,
          limit: jobsResponse.data.data.limit
        });
        
        setCurrentPage(page);
        setError(null);

        if (savedJobsResponse.data.success) {
          const savedJobsList = savedJobsResponse.data.data.jobs;
          const savedJobsMap = {};
          
          savedJobsList.forEach(savedJob => {
            savedJobsMap[savedJob.jobId] = true;
          });

          const updatedSavedJobs = {};
          newJobs.forEach(job => {
            updatedSavedJobs[job.id] = savedJobsMap[job.id] || false;
          });

          setSavedJobs(prev => ({ ...prev, ...updatedSavedJobs }));
        }
      } else {
        setError(jobsResponse.data.message || 'Failed to fetch jobs');
      }
    } catch (err) {
      setError('Error connecting to the server');
      console.error('Error fetching jobs:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchJobs = async (page = 0, append = false) => {
    await fetchJobsAndSavedStatus(page, append);
  };

  const handleSaveJob = async (job) => {
    try {
      const isJobSaved = savedJobs[job.id] || false;
      
      if (!isJobSaved) {
        setSavedJobs(prev => ({ ...prev, [job.id]: true }));
        
        const response = await api1.post('/api/saved-jobs', {
          jobId: job.id,
          title: job.title,
          company: job.company,
          location: job.location,
          description: job.description,
          jobType: job.jobType,
          skills: job.skills || [],
          matchPercentage: job.matchPercentage,
          timePosted: job.timePosted,
          url: job.url,
          sourceUrl: job.sourceUrl,
          remote: job.remote || false,
          salary: job.salary
        });

        if (!response.data.success) {
          setSavedJobs(prev => ({ ...prev, [job.id]: false }));
          console.error('Failed to save job:', response.data.message);
        }
      }
    } catch (error) {
      setSavedJobs(prev => ({ ...prev, [job.id]: false }));
      console.error('Error saving job:', error);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-400 p-8 w-full">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
            <Briefcase className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Recommended Jobs</h2>
            <p className="text-gray-600 text-base">Positions matching your skills and preferences</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <FilterDropdown
              label="Country"
              options={[
                { value: 'us', label: 'United States' },
                { value: 'ca', label: 'Canada' }
              ]}
              value={filters.country}
              onChange={(value) => setFilters({ ...filters, country: value, city: '' })}
              placeholder="Select Country"
            />
          </div>

          <div className="flex-1 min-w-[200px]">
            <FilterDropdown
              label="City"
              options={getCitiesForCountry()}
              value={filters.city}
              onChange={(value) => setFilters({ ...filters, city: value })}
              placeholder="Select City"
              readOnly={!filters.country}
              search
            />
          </div>

          <div className="flex-1 min-w-[200px]">
            <FilterDropdown
              label="Remote"
              options={[
                { value: '', label: 'All' },
                { value: 'remote', label: 'Remote' },
                { value: 'onsite', label: 'On-site' }
              ]}
              value={filters.remote}
              onChange={(value) => setFilters({ ...filters, remote: value })}
              placeholder="All"
            />
          </div>
        </div>
      </div>

      <JobsList
        jobs={jobs}
        savedJobs={savedJobs}
        loading={loading}
        loadingMore={loadingMore}
        error={error}
        currentPage={currentPage}
        pagination={pagination}
        showPagination={jobs.length > 0}
        showRemoveButton={false}
        emptyStateMessage={`No jobs found for '${currentUserJob || 'Software Engineer'}'`}
        onSaveJob={handleSaveJob}
        onPreviousPage={() => fetchJobs(currentPage - 1)}
        onNextPage={() => fetchJobs(currentPage + 1)}
        onRetry={fetchJobs}
      />
    </div>
  );
};

export default AllJobs;
